
import Page from '../Page.vue';
import { notificationStore } from '@/store/index';
import { Component, Mixins } from 'vue-property-decorator';
import { VuetifyMixin, PaginatedTableMixin, CoachRoutingMixin, BAIconsMixin, ColorMixin, BARatingMixin } from '@/mixins';
import { PaginatedResponse } from '@/../types';
import { DataTableHeader } from 'vuetify';
import { MissingDataBehaviour, BARatingColor } from '@best-athletes/ba-types'
import { ObjectCache } from '@/helpers/object-cache';
import { AllRatingFormulaFactors, BARatingFormula, BARatingFormulaFactor, TeamModel } from '@/models';
import { BARatingProfileModel } from '@/models/athlete/BARatingProfileModel'
import { OrganizationModel } from '@/models/organization/OrganizationModel';
import { athleteApi } from '@/api/AthleteApi';
import { baRatingFormulaApi } from '@/api/BARatingFormulaApi';
import { organizationApi } from '@/api/OrganizationApi';
import { DateTime } from 'luxon';
import { wait } from '@/helpers'

@Component({
	components: {
		Page,
	}
})
export default class AthleteRatingsDashboard extends Mixins(VuetifyMixin, PaginatedTableMixin, BARatingMixin, CoachRoutingMixin, BAIconsMixin, ColorMixin){
	MissingDataBehaviour = MissingDataBehaviour;
	MissingDataBehaviours = Object.keys(MissingDataBehaviour);
	BARatingColors = Object.entries(BARatingColor).map(([key, value]) => ({key, value}));

	isFormulaLoaded: boolean = false;
	formula: BARatingFormula;
	backupFormula: BARatingFormula;
	existingFormula: BARatingFormula;

	mounted() {
		this.tableOptions.sortBy = ['rating'];
		this.tableOptions.sortDesc = [true];
		this.localForagePersistFields = [['search', ''],['tableOptions.page', 1],['tableOptions.itemsPerPage', 2]];

		this.loadActiveFormula();
	}

	async initializeBackup() {
		this.backupFormula = Object.assign({}, this.formula);
		this.existingFormula = undefined;
	}
	async loadActiveFormula() {
		if( !this.IsAdmin ) return;
		
		this.isFormulaLoaded = false;
		this.formula = await baRatingFormulaApi.findActive();
		this.isFormulaLoaded = true;
		this.initializeBackup();
	}

	async loadTable(): Promise<void> {
		try {
			this.dataItems = [];
			this.dataItemsCount = 0;

			this.isLoading = true;
			this.isLoaded = false;

			const baRatings: PaginatedResponse<BARatingProfileModel> = await athleteApi.getBARatings(this.filterTeam?.id, this.filterOrg?.id, this.search, this.TableQueryOptions);
			this.dataItems = baRatings.docs;
			this.dataItemsCount = baRatings.total;

			this.isLoaded = true;
			this.isLoading = false;
		} catch(e) {
			notificationStore.pushSnackbarError({message: `Error loading athletes: ${e}`});
		}
	}
	teamsCache: ObjectCache<TeamModel> = new ObjectCache<TeamModel>();

	get TableHeaders(): Array<DataTableHeader<any>> {
		let headers: Array<DataTableHeader<any>> = [
			{ text: 'Name', value: 'lastName', sortable: false, },
			{ text: 'Team', value: 'team', sortable: false, align: 'center' },
			{ text: 'Rating', value: 'rating', sortable: true, align: 'center' },
			// { text: 'gen', value: 'gender', sortable: false, align: 'center' },
			// { text: 'DOB', value: 'birthDate', sortable: false, },
			{ text: 'Data', value: 'data', sortable: false, },
		];
		if( this.IsLargeScreen ) {
			headers.push({text: '', value: 'actions', sortable: false });
			headers.push({text: '', value: 'data-table-expand', sortable: false});
		}
		return headers;
	}

	showFormulaControls: boolean = false;
	showLevelsControls: boolean = false;

	showFilters: boolean = false;

	RatingColor(rating: number, formula: BARatingFormula): string {
		for( const level of formula.levelsSorted ) {
			if( rating >= level.floor ) return level.color;
		}
		return 'primary';
	}
	RatingTextColor(rating: number, formula: BARatingFormula): string {
		return this.ComplementaryTextColor(this.RatingColor(rating, formula));
	}

	showConfirmSaveDlg: boolean = false;
	showConfirmDeleteDlg: boolean = false;
	async saveFormula(active: boolean = false) {
		try {
			// if overwriting an existing formula, update the ids
			if( this.IsNotEmpty(this.existingFormula) ) {
				this.formula['_id'] = this.existingFormula['_id'];
				this.formula['id'] = this.existingFormula['id'];
				this.formula.active = this.existingFormula.active;
			}
			// if the title or version has changed, save a new formula entry
			else if( this.backupFormula.title != this.formula.title || this.backupFormula.version != this.formula.version ) {
				delete this.formula['_id'];
				delete this.formula['id'];
				this.formula.active = active;
			}

			this.formula.created = new Date();
			this.formula = await baRatingFormulaApi.save(this.formula);
			notificationStore.pushSnackbarSuccess({message: `Formula ${this.formula.title} v${this.formula.version} saved`})
		} catch(e) {
			notificationStore.pushSnackbarError({message: `Error saving Formula: ${e}`})
		}
		await this.initializeBackup();
		await this.updateFilter();
	}
	async onFormulaTest() {
		await this.updateFilter();
	}
	async onFormulaSave() {
		this.existingFormula = await baRatingFormulaApi.exists(this.formula);
		if( this.IsNotEmpty(this.existingFormula) ) {
			this.showConfirmSaveDlg = true;
			return;
		}
		await this.saveFormula();
	}
	async onConfirmSaveCancel() {
		this.showConfirmSaveDlg = false;
	}
	async onConfirmSaveAccept() {
		await this.saveFormula();
		this.showConfirmSaveDlg = false;
	}
	async onConfirmSaveUpdateVersion() {
		this.formula.version = this.formula.version + 1;
		this.showConfirmSaveDlg = false;
		await this.onFormulaSave();
	}

	async loadFormulas() {
		this.formulasLoaded = false;
		this.formulasLoading = true;

		const response: PaginatedResponse<BARatingFormula> = await baRatingFormulaApi.queryAll({},{sort:{ fields: [{ field: 'created', desc: true }, { field: 'version', desc: true}] }});
		this.allFormulas = response.docs;

		this.formulasLoading = false;
		this.formulasLoaded = true;
	}

	formulaToDelete: BARatingFormula;
	async onFormulaDelete(formula: BARatingFormula) {
		if( this.IsEmpty(formula) ) return;
		this.formulaToDelete = formula;
		this.showConfirmDeleteDlg = true;
	}
	async onConfirmDeleteCancel() {
		this.showConfirmDeleteDlg = false;
	}
	async onConfirmDeleteAccept() {
		await baRatingFormulaApi.delete(this.formulaToDelete)
		this.showConfirmDeleteDlg = false;
		await this.loadFormulas();
	}

	showArchivedFormulas: boolean = false;
	async onFormulaArchive(formula: BARatingFormula) {
		if( this.IsEmpty(formula) || formula.archive ) return;
		formula.archive = true;
		formula = await baRatingFormulaApi.save(formula);
	}
	async onFormulaUnarchive(formula: BARatingFormula) {
		if( this.IsEmpty(formula) || !formula.archive ) return;
		formula.archive = false;
		formula = await baRatingFormulaApi.save(formula);
	}

	showAddFactorDlg: boolean = false;
	newFactor: BARatingFormulaFactor;
	isFactorChanging: boolean = false;
	async onAddFactorBegin() {
		this.showAddFactorDlg = true;
	}
	async onAddFactorAccept() {
		this.formula.factors.push(this.newFactor);
		this.showAddFactorDlg = false;
		this.newFactor = undefined;
		await this.onFormulaTest();
	}
	async onSelectNewFactor(item) {
		this.isFactorChanging = true;
		this.newFactor = new BARatingFormulaFactor().load(item);
		this.isFactorChanging = false;
	}
	get AvailableFactors(): Array<Partial<BARatingFormulaFactor>> {
		return AllRatingFormulaFactors.filter(f => !this.formula.factors.some(ff => ff.key === f.key));
	}
	async onRemoveFactor(item: BARatingFormulaFactor) {
		this.isFactorChanging = true;
		if( confirm(`Remove ${item.title}? This action cannot be undone`) ) {
			this.formula.factors = this.formula.factors.filter(factor => factor.key != item.key);
		}
		this.isFactorChanging = false;
	}

	showNewFormulaDlg: boolean = false;
	newFormulaTitle: string;
	isCreatingNewFormula: boolean = false;
	async onFormulaCreateBegin() {
		const now: Date = new Date();
		this.newFormulaTitle = `New Formula ${DateTime.fromJSDate(now).toFormat("MMM dd yyyy")}`;
		this.showNewFormulaDlg = true;
	}
	async onNewFormulaAccept() {
		this.isCreatingNewFormula = true;
		this.formula = new BARatingFormula();
		this.formula.title = this.newFormulaTitle;
		this.isCreatingNewFormula = false;
		this.showNewFormulaDlg = false;
		notificationStore.pushSnackbarSuccess({message: `Created ${this.formula.title}`});
		await this.onFormulaTest();
	}
	async onFormulaCopyBegin() {
		this.isCreatingNewFormula = true;
		delete this.formula['_id'];
		delete this.formula['id'];
		this.formula.active = false;
		this.formula.created = new Date();
		this.formula.title = `Copy of ${this.formula.title}`;
		this.formula.version = 1;
		notificationStore.pushSnackbarSuccess({message: `Created ${this.formula.title}`});
		this.isCreatingNewFormula = false;
	}

	showFormulaSelection: boolean = false;
	allFormulas: Array<BARatingFormula> = [];
	formulasLoaded: boolean = false;
	formulasLoading: boolean = false;
	get AllFormulas(): Array<BARatingFormula> {
		if( this.showArchivedFormulas ) return this.allFormulas;
		return this.allFormulas.filter(f => !f.archive);
	}
	async onFormulaLoad(formula: BARatingFormula) {
		this.formulasLoading = true;
		if( this.formula.title !== formula.title || this.formula.version !== formula.version ) {
			this.formula = formula;
		}
		this.formulasLoading = false;
		this.showFormulaSelection = false;
		await this.onFormulaTest();
	}
	async onFormulaSetActive(formula: BARatingFormula) {
		const foundFormula = this.allFormulas.find(f => (f.title === formula.title) && (f.version === formula.version));
		if( foundFormula ) foundFormula.active = true;
		else {
			formula = await baRatingFormulaApi.save(formula);
		}
		await baRatingFormulaApi.setActive(formula);
		await this.loadFormulas();
		await this.loadActiveFormula();
		await this.onFormulaTest();
	}
	async onFormulaSelectStart() {
		this.loadFormulas();
		this.showFormulaSelection = true;
	}
	get FormulaHeaders(): Array<DataTableHeader<any>> {
		let headers: Array<DataTableHeader<any>> = [
			{ text: 'Title', value: 'title', sortable: false },
			{ text: 'Ver', value: 'version', sortable: false, align: 'center' },
			{ text: 'Created', value: 'created', sortable: false },
			{ text: 'Factors', value: 'factors', sortable: false, align: 'center' },
			{ text: 'Levels', value: 'levels', sortable: false },
		];
		if( this.IsLargeScreen ) {
			headers.push({text: '', value: 'actions', sortable: false });
			headers.push({text: '', value: 'data-table-expand', sortable: false});
		}
		return headers;
	}

	isLevelColorChanging: boolean = false;
	async onSelectLevelColor() {
		this.isLevelColorChanging = true;
		await wait(250);
		this.isLevelColorChanging = false;
	}

	filterTeam: TeamModel = undefined;
	async onUpdateFilterTeam(team: TeamModel) {
		this.filterTeam = team;
		this.updateSearch();
	}

	filterOrg: OrganizationModel = undefined;
	filterOrgTeams: Array<TeamModel> = [];
	async onUpdateFilterOrg(org: OrganizationModel) {
		this.filterTeam = undefined;
		this.filterOrg = org;
		if( this.IsNotEmpty(org) ) this.filterOrgTeams = await organizationApi.getOrganizationTeams({organizationId: org.id});
		
		this.updateSearch();
	}

	FactorsDisplay(item: BARatingProfileModel): Array<BARatingFormulaFactor> {
		if( item.isCondensedDisplay ) return this.formula.factors.slice(0,5);
		return this.formula.factors;
	}
}
